import { useState } from 'react';
import { useCompanyContext } from '@/hooks/use-company-context';
import { useGpsDeviceManagement } from '@/hooks/useGpsDeviceManagement';
import { GpsLicenseGate } from '@/components/gps/GpsLicenseGate';
import { DeviceSimpleList } from '@/components/gps/DeviceSimpleList';
import { DeviceDetailsSheet } from '@/components/gps/DeviceDetailsSheet';
import { DeviceFormSheet } from '@/components/gps/DeviceFormSheet';
import { GpsDevice } from '@/types/gps';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Smartphone, RefreshCw, AlertTriangle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export default function GpsDevicesPage() {
  const { companyId } = useCompanyContext();
  
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<GpsDevice | undefined>();
  const [deletingDeviceId, setDeletingDeviceId] = useState<string | null>(null);
  const [detailsDevice, setDetailsDevice] = useState<GpsDevice | null>(null);

  // Buscar dados da empresa
  const { data: company } = useQuery({
    queryKey: ['company-data', companyId],
    queryFn: async () => {
      if (!companyId) return null;
      const { data } = await supabase
        .from('companies')
        .select('razao_social, nome_fantasia, name')
        .eq('id', companyId)
        .single();
      return data;
    },
    enabled: !!companyId,
  });

  const {
    devices,
    isLoading,
    createDevice,
    updateDevice,
    deleteDevice,
  } = useGpsDeviceManagement(companyId);

  // Calcular equipamentos não instalados
  const uninstalledDevices = devices?.filter(d => !d.vehicle_id) || [];
  const hasUninstalled = uninstalledDevices.length > 0;

  const handleCreate = () => {
    setEditingDevice(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (device: GpsDevice) => {
    setEditingDevice(device);
    setIsFormOpen(true);
  };

  const handleSubmit = (data: Partial<GpsDevice>) => {
    if (editingDevice) {
      updateDevice.mutate(
        { id: editingDevice.id, ...data },
        { onSuccess: () => setIsFormOpen(false) }
      );
    } else {
      createDevice.mutate(
        { 
          ...data, 
          company_id: companyId! 
        } as Omit<GpsDevice, 'id' | 'created_at' | 'updated_at'>,
        { onSuccess: () => setIsFormOpen(false) }
      );
    }
  };

  const handleDelete = () => {
    if (deletingDeviceId) {
      deleteDevice.mutate(deletingDeviceId, {
        onSuccess: () => setDeletingDeviceId(null),
      });
    }
  };

  return (
    <GpsLicenseGate>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Equipamentos GPS</h1>
            <p className="text-sm text-muted-foreground">
              Gerencie os equipamentos GPS da frota
            </p>
          </div>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Equipamento
          </Button>
        </div>

        {/* Device Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Carregando equipamentos...</p>
          </div>
        ) : (
          <DeviceSimpleList
            devices={devices || []}
            onDeviceClick={setDetailsDevice}
          />
        )}

        {/* Device Form Sheet */}
        <DeviceFormSheet
          open={isFormOpen}
          onOpenChange={setIsFormOpen}
          companyId={companyId || ''}
          device={editingDevice}
          onSubmit={handleSubmit}
          isLoading={createDevice.isPending || updateDevice.isPending}
        />

        {/* Device Details Sheet */}
        <DeviceDetailsSheet
          device={detailsDevice}
          open={!!detailsDevice}
          onOpenChange={(open) => !open && setDetailsDevice(null)}
          onEdit={handleEdit}
          onDelete={setDeletingDeviceId}
        />

        {/* Delete Confirmation */}
        <AlertDialog open={!!deletingDeviceId} onOpenChange={() => setDeletingDeviceId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir Equipamento GPS</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir este equipamento? Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive">
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </GpsLicenseGate>
  );
}
